// ============================================================================
// ORDERS · Order Desk — is the foundation deployed? (B2a)
// ============================================================================
// The Desk's tables exist since P0, but B2a's package adds the columns an order
// actually needs. Until David runs it the Desk cannot store an order, so the
// world stays shut rather than offering a form that would fail on save.
//
// HOW IT DETECTS THAT: one probe read that names a column the package adds.
// PostgREST answers a missing column with 42703 / "does not exist", the same
// shape useOrdersData.js:30 already treats as "the package has not been run".
// A column probe, not a table probe — orders_pis has existed since 2026-08-31,
// so `from('orders_pis').select('id')` would succeed and say nothing.
//
// The distinction that matters: "not deployed" is a state the UI explains,
// while a network or permission failure is an ERROR and must not be dressed up
// as a missing migration (the 06.09 incident, where a slow read read as "not
// imported"). Anything that is not recognisably a schema gap is rethrown.
// ============================================================================

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../../supabase';

/** True when the failure means "the B2a package has not been run yet". */
export const isMissingDeskSchema = (err) => {
  const m = `${err?.message || ''} ${err?.code || ''}`.toLowerCase();
  return m.includes('does not exist')
      || m.includes('42703')            // undefined_column — the expected answer
      || m.includes('pgrst204')         // column not found in schema cache
      || m.includes('pgrst205')         // table not found in schema cache
      || m.includes('schema cache');
};

export function useDeskReady({ enabled = true } = {}) {
  const q = useQuery({
    queryKey: ['orders', 'desk', 'ready'],
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      // Names three of the package's columns. One row is enough; zero rows is
      // a perfectly good answer — we are asking about the shape, not the data.
      const { error } = await supabase
        .from('orders_pis')
        .select('id,sample_requirement,sample_note,sent_at')
        .limit(1);
      if (!error) return { ready: true };
      if (isMissingDeskSchema(error)) return { ready: false, notDeployed: true };
      throw error;
    },
  });

  return {
    ready: q.data?.ready === true,
    notDeployed: q.data?.notDeployed === true,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}
